import { listDispositionsForWorkflow } from '@100mslive/disposition-engine';
import type { Campaign } from '@compass/client/types/campaign';
import type { GetCaseRecordResponse } from '@compass/client/types/case_record';
import type { User } from '@compass/client/types/user';
import type { Workflow } from '@compass/client/types/workflow';
import { DrugOutputFieldPreview, getPreviewComponent } from '@components/fields';
import { Badge } from '@components/ui/badge';
import { SLATimer } from '@components/ui/sla-timer';
import { formatCaseId } from '@utils/cases';
import { formatComplexValue } from '@utils/formatValue';
import { collectEntityLeafPills, formatEntityLeafCopyValue } from '@utils/paStatusColumns';
import { get } from 'lodash-es';
import {
  AlertCircle,
  ArrowLeftToLine,
  ArrowRightFromLine,
  Headphones,
  List,
  Loader2,
  MoreVertical,
  PhoneForwarded,
  Plus,
  Share2,
} from 'lucide-react';
import React, { useState } from 'react';

import { AddNoteDialog } from '@/components/AddNoteDialog';
import { AssigneeSelect } from '@/components/AssigneeSelect';
import { CloneCaseMenuItem } from '@/components/CloneCaseMenuItem';
import { FormattedDateRelative } from '@/components/FormattedDateRelative';
import { RelativeTimeAgo } from '@/components/RelativeTimeAgo';
import { StatusDropdown } from '@/components/StatusDropdown';
import { SimpleTooltip } from '@/components/ui/tooltip';
import { UserCell } from '@/components/UserCell';
import { ViewNotesSheet } from '@/components/ViewNotesSheet';
import {
  DISPOSITION_WORKFLOW_TYPES,
  isWarmTransferVariant,
  WorkflowType,
} from '@/constants/dispositions';
import { workflowTypeLabel } from '@/constants/workflows';
import type { UseAssigneeOptionsReturn } from '@/hooks/useAssigneeOptions';
import { resolveEngineWorkflowType } from '@/lib/disposition/engineWorkflowType';
import { PayerHoursPopover } from '@/pages/workforce/components/PayerHoursPopover';
import type { ActionDataParams } from '@/types/actions';
import type { ColumnConfig, TableColumnConfig, TableConfig } from '@/types/table';
import { canAddNoteToCase } from '@/utils/caseStateHelpers';
import { copyToClipboard } from '@/utils/clipboard';
import { isSystemDispositionCode } from '@/utils/dispositions';
import { extractPayerHoursSlug, resolvePayerLabel } from '@/utils/payer';
import { buildCaseUrl } from '@/utils/routes';

import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const isEmpty = (value: unknown): boolean => {
  return value === null || value === undefined || value === '';
};

// Row actions dropdown component for the three-dot menu
const RowActionsDropdown: React.FC<{
  item: GetCaseRecordResponse;
}> = ({ item }) => {
  const [isAddNoteDialogOpen, setIsAddNoteDialogOpen] = useState(false);
  const [isViewNotesSheetOpen, setIsViewNotesSheetOpen] = useState(false);

  // Check if case is in a state that allows adding notes
  const canAddNote = canAddNoteToCase(item.state);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 hover:bg-muted"
            onClick={e => e.stopPropagation()}
          >
            <MoreVertical className="h-4 w-4" />
            <span className="sr-only">Open actions menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={e => e.stopPropagation()}>
          {/* Add Note */}
          <DropdownMenuItem
            onClick={() => setIsAddNoteDialogOpen(true)}
            className="cursor-pointer"
            disabled={!canAddNote}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Note
          </DropdownMenuItem>

          {/* View Notes */}
          <DropdownMenuItem
            onClick={() => setIsViewNotesSheetOpen(true)}
            className="cursor-pointer"
          >
            <List className="mr-2 h-4 w-4" />
            View Notes
          </DropdownMenuItem>

          {/* Copy Link */}
          <DropdownMenuItem
            onClick={e => {
              e.stopPropagation();
              const caseUrl = buildCaseUrl(item);
              copyToClipboard(caseUrl, 'Link copied');
            }}
            className="cursor-pointer"
          >
            <Share2 className="mr-2 h-4 w-4" />
            Copy Link
          </DropdownMenuItem>

          {/* Clone Case - only for orgs with feature_flag/clone_case_<org_id> = true */}
          <CloneCaseMenuItem caseId={item.id!} organizationId={item.organization_id} />
        </DropdownMenuContent>
      </DropdownMenu>

      <AddNoteDialog
        isOpen={isAddNoteDialogOpen}
        onClose={() => setIsAddNoteDialogOpen(false)}
        caseRecordId={item.id || ''}
      />

      <ViewNotesSheet
        isOpen={isViewNotesSheetOpen}
        onClose={() => setIsViewNotesSheetOpen(false)}
        caseRecordId={item.id || ''}
        canAddNote={canAddNote}
      />
    </>
  );
};

/**
 * Resolvers for cross-program views (e.g. My Cases) where a single Workflow is not
 * available. Each resolver is optional; columns that need one fall back to "N/A"
 * (or the workflow-derived value, where applicable) when it isn't provided.
 */
export interface CasesTableContext {
  /** Program ID → program name. Drives the optional `program` column. */
  programsById?: Map<string, string>;
  /** Workflow ID → SLA minutes. Drives the SLA timer column when no single
   * `selectedWorkflow` is in scope (cross-program views). */
  slaByWorkflowId?: Map<string, number>;
  /** Workflow ID → version (e.g. 'v3'). Drives the v3/v4 breach-soon detection in
   * the SLA timer column when no single `selectedWorkflow` is in scope. */
  versionByWorkflowId?: Map<string, string>;
  /** Payor slug → display name. Drives the optional `payor` column. */
  payorBySlug?: Map<string, string>;
  /** Workflow ID → workflow name. Drives the optional `manufacturer` column in
   * cross-program PQC views, where each workflow's name is the manufacturer. */
  workflowNameById?: Map<string, string>;
  /** Force PQC rendering (ack-aware SLA column + per-tab lifecycle columns) in a
   * cross-program PQC view where `selectedWorkflow` is null (Tier-2 org page).
   * Per-row data still comes from each case's workflow_type/output_data. */
  pqcView?: boolean;
  /** Workflow `ui.status` config used to color/label the Status badge in
   * cross-program views (where there's no single `selectedWorkflow`). */
  statusConfig?: Record<string, any>;
  /** Org-wide assignee options injected into the `assigned` column's
   * `AssigneeSelect` for cross-program views (Tier-2 org page), where the
   * program-scoped `useAssigneeOptions` has no selected program to read from. */
  assigneeOptions?: UseAssigneeOptionsReturn;
  /** Show the payer-hours clock beside the payor name (My Cases only, BV-2654). */
  showPayerHours?: boolean;
}

// IDs of cross-program opt-in columns. They are only added to standardColumns
// when the caller passes them in `includeColumns`. Default views (e.g. CaseList)
// are scoped to a single program/workflow and don't want them; cross-program
// views (e.g. My Cases) opt in explicitly.
//
// Note on identifiers: each id here doubles as the `ColumnConfig.fieldPath`
// value AND the `case` discriminator in `createColumnFromConfig`'s switch —
// the opt-in column accessors read from the case record directly (no
// `get(item, fieldPath)`). The rendered `TableColumnConfig.key` is separate
// and may be camelCased (e.g. id `'workflow_type'` → key `'workflowType'`);
// `excludeColumns` filters on id, while the key is what GenericTable uses
// internally for column identity.
// 'total_call_attempts' is opt-in because only the program case-list endpoints fill
// ai_call_attempts/human_call_attempts; on any other view the row would read 0 whatever its calls.
const OPT_IN_COLUMN_IDS = [
  'program',
  'workflow_type',
  'payor',
  'manufacturer',
  'total_call_attempts',
] as const;

/**
 * Disposition code -> label, resolved from the ROW's own workflow type.
 *
 * The column used to depend entirely on a `dispositionsMap` the caller threads in, and
 * only `CasesContainer` ever did — so Campaign Details, Tier 2 Cases and My Cases all
 * rendered the bare numeric code where a label belongs. Resolving here removes the
 * footgun: a new caller cannot silently regress the column by forgetting an argument.
 *
 * Per row rather than per table, because the cross-program views (My Cases, Tier 2 org)
 * mix workflow types and a single map keyed off one of them would mislabel the rest.
 *
 * Cached per workflow type: `listDispositionsForWorkflow` rebuilds its catalog on every
 * call and this runs once per rendered cell.
 */
const dispositionCatalogCache = new Map<string, Record<number, string>>();

const lookupDispositionLabel = (
  workflowType: string | undefined,
  code: number,
): string | undefined => {
  if (!workflowType) return undefined;
  let catalog = dispositionCatalogCache.get(workflowType);
  if (!catalog) {
    try {
      catalog = listDispositionsForWorkflow(resolveEngineWorkflowType(workflowType));
    } catch {
      // Unknown workflow type — degrade to the raw code rather than throwing, the same
      // way useDispositions returns an empty catalog for legacy workflow types.
      catalog = {};
    }
    dispositionCatalogCache.set(workflowType, catalog);
  }
  return catalog[code];
};

// Helper to get a call detail field with fallback to legacy nested path (input_data.data.call_details.X)
const getCallDetailValue = (item: GetCaseRecordResponse, field: string): any => {
  const flatValue = get(item, `input_data.data.${field}`);
  if (flatValue !== undefined && flatValue !== null) return flatValue;
  return get(item, `input_data.data.call_details.${field}`);
};

// Create column from config
const createColumnFromConfig = ({
  config,
  selectedWorkflow,
  onActionSelect,
  onAssigneeSelect,
  onMarkAsAE,
  markingAsAECaseId,
  usersMap,
  usersLoading,
  dispositionsMap,
  context,
}: {
  config: ColumnConfig;
  selectedWorkflow?: Workflow | null;
  onActionSelect?: (action: string, caseId: string, actionDataParams?: ActionDataParams) => void;
  onAssigneeSelect?: (assigneeUserId: string, caseId: string, teamId?: string) => void;
  onMarkAsAE?: (caseId: string) => void;
  markingAsAECaseId?: string | null;
  usersMap?: Map<string, User>;
  usersLoading?: boolean;
  dispositionsMap?: Map<number, string>;
  context?: CasesTableContext;
}): TableColumnConfig<GetCaseRecordResponse> | null => {
  // Same rule the timer column applies: a single-workflow view knows its type up front,
  // a cross-program one has to read it off each case.
  const resolveRowWorkflowType = (item: GetCaseRecordResponse): string | undefined =>
    selectedWorkflow?.workflow_type ?? item.workflow_type;

  // Standard columns
  if (config.type === 'standard') {
    switch (config.fieldPath) {
      case 'id': {
        // For PQC the Case ID column is sticky-left; lock its width (140px) so the
        // sla_status sticky offset (also 140px) lines up exactly with its right edge.
        // Honor the cross-program org PQC view (context.pqcView) where selectedWorkflow is null.
        const isPqc = selectedWorkflow?.workflow_type === WorkflowType.PQC || !!context?.pqcView;
        return {
          key: 'caseId',
          header: 'Case ID',
          width: isPqc ? 140 : undefined,
          accessor: item => {
            const creationMethod = item.creation_method ?? '';
            const isRecording = creationMethod === 'recording';
            return (
              <div className="flex items-center gap-1.5">
                <span
                  className="font-medium cursor-pointer hover:underline font-mono text-xs"
                  title="Click to view case details"
                >
                  {formatCaseId(item.id, item.code)}
                </span>
                {isRecording && (
                  <span title="Created from recording">
                    <Headphones className="h-3.5 w-3.5 text-muted-foreground" />
                  </span>
                )}
                {item.error?.message && (
                  <SimpleTooltip content={item.error.message}>
                    <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                  </SimpleTooltip>
                )}
              </div>
            );
          },
          copyable: true,
          copyValue: item => item.code || item.id || '',
        };
      }
      case '': {
        // Resolve SLA per-row from the workflow map when no single workflow is selected
        // (cross-program views). Falls back to selectedWorkflow.sla?.minutes for the
        // single-workflow case (CaseList).
        const resolveSlaMinutes = (item: GetCaseRecordResponse): number | undefined => {
          const fromMap = item.workflow_id
            ? context?.slaByWorkflowId?.get(item.workflow_id)
            : undefined;
          return fromMap ?? selectedWorkflow?.sla?.minutes;
        };
        // Resolve workflow_type per-row from the case itself in cross-program views;
        // fall back to selectedWorkflow.workflow_type for single-workflow views.
        const resolveWorkflowType = (item: GetCaseRecordResponse): string | undefined =>
          selectedWorkflow?.workflow_type ?? item.workflow_type;
        // Resolve workflow version per-row from the version map in cross-program
        // views; fall back to selectedWorkflow.version for single-workflow views.
        // Drives v3/v4 breach-soon detection inside SLATimer.
        const resolveWorkflowVersion = (item: GetCaseRecordResponse): string | undefined =>
          (item.workflow_id ? context?.versionByWorkflowId?.get(item.workflow_id) : undefined) ??
          selectedWorkflow?.version;

        // PQC view: either a single PQC workflow is selected, or the cross-program
        // Tier-2 org view forces it via context.pqcView (selectedWorkflow is null there).
        const isPqc = selectedWorkflow?.workflow_type === WorkflowType.PQC || !!context?.pqcView;
        // PQC gets the redesigned column (key + header + ack-aware props). All other
        // workflows keep the original `timer` column with `Time remaining` header and
        // the legacy SLATimer rendering (handled inside SLATimer itself via workflowType).
        if (!isPqc) {
          return {
            key: 'timer',
            header: 'Time remaining',
            accessor: item => (
              <div className="flex items-center gap-2">
                <SLATimer
                  workflowLastStartedAt={item.workflow_last_started_at}
                  workflowDurationSeconds={item.workflow_duration_seconds}
                  slaMinutes={resolveSlaMinutes(item)}
                  isBreached={item.status_flags?.sla_breach}
                  completedAt={item.workflow_completed_at || item.workflow_cancelled_at}
                  workflowState={item.workflow_state}
                  workflowType={resolveWorkflowType(item)}
                  caseState={item.state}
                  slaRiskEscalated={item.status_flags?.sla_risk_escalated}
                  workflowVersion={resolveWorkflowVersion(item)}
                />
              </div>
            ),
          };
        }
        // Lock SLA Status width so its right edge is at a predictable position
        // and the Assignee column starts at a stable offset. Width tuned to fit
        // the longest content: running_near_breach shows icon + timer +
        // "SLA Breach Soon" badge (~190px content area).
        return {
          key: 'sla_status',
          // "Time Remaining" is the Org-wide Cases label; the standard
          // (flag-off) view keeps the original "SLA Status" header.
          header: context?.pqcView ? 'Time Remaining' : 'SLA Status',
          width: 210,
          accessor: item => {
            const outputData = item.output_data?.data as Record<string, unknown> | undefined;
            const decision =
              typeof outputData?.decision === 'string'
                ? (outputData.decision as string)
                : undefined;
            const ackId =
              typeof outputData?.ack_id === 'string' ? (outputData.ack_id as string) : undefined;
            // Warm transfer is record-only — it has no SLA. Show its Ack ID (like other variants
            // surface via the SLA timer) with a warm-transfer icon + tooltip as the marker; fall
            // back to a "Warm Transfer" label when no Ack ID is present.
            const isWarmTransfer = isWarmTransferVariant(selectedWorkflow?.variant);
            return (
              <div className="flex items-center gap-2">
                {isWarmTransfer ? (
                  <SimpleTooltip content="Warm Transfer — record only">
                    <span className="inline-flex items-center gap-1.5 text-muted-foreground text-xs">
                      <PhoneForwarded className="h-3.5 w-3.5 shrink-0" />
                      {ackId ? (
                        <span className="truncate" title={ackId}>
                          {ackId}
                        </span>
                      ) : (
                        'Warm Transfer'
                      )}
                    </span>
                  </SimpleTooltip>
                ) : (
                  <SLATimer
                    workflowLastStartedAt={item.workflow_last_started_at}
                    workflowDurationSeconds={item.workflow_duration_seconds}
                    slaMinutes={resolveSlaMinutes(item)}
                    isBreached={item.status_flags?.sla_breach}
                    completedAt={item.workflow_completed_at || item.workflow_cancelled_at}
                    workflowState={item.workflow_state}
                    workflowType={resolveWorkflowType(item)}
                    caseState={item.state}
                    stateUpdatedAt={item.state_updated_at}
                    decision={decision}
                    ackId={ackId}
                    slaRiskEscalated={item.status_flags?.sla_risk_escalated}
                    workflowVersion={resolveWorkflowVersion(item)}
                  />
                )}
              </div>
            );
          },
        };
      }

      case 'program':
        return {
          key: 'program',
          header: 'Program',
          accessor: item => {
            const name = item.program_id ? context?.programsById?.get(item.program_id) : undefined;
            if (!name) return <span className="text-muted-foreground">N/A</span>;
            return (
              <span className="whitespace-nowrap text-foreground" title={name}>
                {name}
              </span>
            );
          },
        };

      case 'workflow_type':
        return {
          key: 'workflowType',
          header: 'Workflow',
          accessor: item => {
            if (!item.workflow_type) return <span className="text-muted-foreground">N/A</span>;
            return (
              <Badge variant="secondary" className="whitespace-nowrap font-normal">
                {workflowTypeLabel(item.workflow_type)}
              </Badge>
            );
          },
        };

      case 'payor':
        return {
          key: 'payor',
          header: 'Payor',
          accessor: item => {
            // resolvePayerLabel handles the PBM/MedBV field-name fork (PBM uses
            // `payor_name`; MedBV uses `primary_payer_name` with `payor_name` as
            // fallback) and falls back to a slug-derived label when the payor
            // isn't in the map yet.
            const label = resolvePayerLabel(
              item.workflow_type,
              item.input_data?.data as Record<string, unknown> | undefined,
              context?.payorBySlug,
            );
            if (!label) return <span className="text-muted-foreground">N/A</span>;
            const labelEl = (
              <span className="whitespace-nowrap text-foreground" title={label}>
                {label}
              </span>
            );
            // Same clock popover as the Workforce Console case cards (BV-2654).
            const workflowType = resolveRowWorkflowType(item) ?? '';
            const payerSlug = context?.showPayerHours
              ? extractPayerHoursSlug(workflowType, item.input_data?.data)
              : '';
            if (!payerSlug) return labelEl;
            return (
              <div className="group flex items-center gap-1">
                {labelEl}
                <PayerHoursPopover
                  caseItem={{
                    organizationId: item.organization_id,
                    programId: item.program_id,
                    payerSlug,
                    workflowType,
                  }}
                  // Same muted shade as the table's copy icons.
                  triggerClassName="text-muted-foreground"
                />
              </div>
            );
          },
        };
      case 'manufacturer':
        return {
          key: 'manufacturer',
          header: 'Manufacturer',
          accessor: item => {
            // Each PQC workflow's name is the manufacturer. Resolved per-row by
            // workflow_id from a name map the caller builds from the org workflow
            // list; falls back to the workflow-type label when unmapped.
            const name = item.workflow_id
              ? context?.workflowNameById?.get(item.workflow_id)
              : undefined;
            const label = name || workflowTypeLabel(item.workflow_type);
            if (!label) return <span className="text-muted-foreground">N/A</span>;
            return (
              <span className="whitespace-nowrap text-foreground" title={label}>
                {label}
              </span>
            );
          },
        };
      case 'reference_id':
        return {
          key: 'referenceId',
          header: 'Reference ID',
          accessor: item =>
            item.reference_id ? (
              <span className="whitespace-nowrap font-mono text-xs" title="Reference ID">
                {item.reference_id}
              </span>
            ) : (
              <span className="text-muted-foreground">N/A</span>
            ),
          copyable: true,
          copyValue: item => item.reference_id || '',
        };

      case 'assigned': {
        // PQC sticky `sla_status` column to the left visually clips the Assignee header's
        // leading character; pl-4 pushes header/cell content past the sticky boundary.
        const isPqcAssignee = selectedWorkflow?.workflow_type === WorkflowType.PQC;
        return {
          key: 'assigned',
          header: 'Assignee',
          // Cap width so Assignee doesn't dominate the row; the assignee chip
          // truncates within this bound. Universal across workflows.
          width: 220,
          headerClassName: isPqcAssignee ? 'pl-4' : undefined,
          // The `width: 220` above bounds the cell box, but the cell is `whitespace-nowrap` and
          // was not clipping: a long assignee email rendered past the cell's right edge and over
          // the next column. AssigneeSelect now truncates its own content; `overflow-hidden`
          // here keeps anything that still can't shrink inside the 220px bound. (The inline
          // dropdown variant sets `ring-0`, so there is no focus ring for this to clip.)
          className: isPqcAssignee ? 'pl-4 overflow-hidden' : 'overflow-hidden',
          accessor: item => (
            <AssigneeSelect
              item={item}
              onAssigneeSelect={onAssigneeSelect}
              optionsOverride={context?.assigneeOptions}
            />
          ),
        };
      }

      case 'state':
        return {
          key: 'status',
          header: 'Status',
          accessor: item => (
            <StatusDropdown
              item={item}
              selectedWorkflow={selectedWorkflow}
              statusConfig={context?.statusConfig}
              onActionSelect={onActionSelect}
            />
          ),
        };

      case 'row_actions':
        return {
          key: 'row_actions',
          header: '',
          accessor: item => (
            <div onClick={e => e.preventDefault()}>
              <RowActionsDropdown item={item} />
            </div>
          ),
        };

      case 'actions':
        return {
          key: 'actions',
          header: '',
          accessor: item => {
            const isLoading = markingAsAECaseId === item.id;
            return (
              <Button
                size="sm"
                className="h-6 px-2 rounded text-xs"
                onClick={e => {
                  e.stopPropagation(); // Prevent row click
                  if (onMarkAsAE && item.id) {
                    onMarkAsAE(item.id);
                  }
                }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    Marking...
                  </>
                ) : (
                  'Mark as AE'
                )}
              </Button>
            );
          },
        };

      case 'created_by':
        return {
          key: 'createdBy',
          header: 'Created by',
          // Cap width universally; long names/emails truncate within this bound.
          width: 220,
          className: 'max-w-[220px] truncate',
          accessor: item => {
            const userId = item.created_by;
            return (
              <UserCell
                user={userId ? usersMap?.get(userId) : undefined}
                fallbackId={userId}
                loading={!!userId && usersLoading}
                nameOnly
              />
            );
          },
        };

      // AI + human call records on the case, the same counts the workforce views show
      // separately (redials and deleted calls are left out server-side).
      case 'total_call_attempts':
        return {
          key: 'totalCallAttempts',
          header: 'Total Call Attempts',
          accessor: item => (item.ai_call_attempts ?? 0) + (item.human_call_attempts ?? 0),
        };

      case 'created_at':
        return {
          key: 'createdAt',
          header: 'Created at',
          accessor: item => <FormattedDateRelative value={item.created_at} />,
          sortable: true,
          sortField: 'created_at',
        };

      // PQC lifecycle timestamps (output_data, populated by PQCAgent). Hardcoded
      // per-tab columns — not user-customizable. Stored as ISO-8601 strings and
      // shown as elapsed time ("Xh Ym ago") with an absolute-time hover tooltip.
      case 'output_data.data.ack_received_at': {
        return {
          key: 'ackReceivedAt',
          header: 'Ack Received',
          accessor: item => {
            const value = (item.output_data?.data as Record<string, unknown> | undefined)
              ?.ack_received_at;
            return <RelativeTimeAgo value={value ? String(value) : undefined} />;
          },
        };
      }

      case 'output_data.data.follow_up_requested_at': {
        return {
          key: 'followUpRequestedAt',
          header: 'Follow-up Requested',
          accessor: item => {
            const value = (item.output_data?.data as Record<string, unknown> | undefined)
              ?.follow_up_requested_at;
            return <RelativeTimeAgo value={value ? String(value) : undefined} />;
          },
        };
      }

      // PQC "Submitted At" — uses state_updated_at (equals the submission time
      // while the case is in the submitted state). Elapsed-time label + tooltip.
      case 'state_updated_at':
        return {
          key: 'submittedAt',
          header: 'Submitted At',
          accessor: item => <RelativeTimeAgo value={item.state_updated_at} />,
        };

      // PQC "Acknowledgement ID" — hardcoded for the cross-program org view (the
      // program page sources the same output_data.data.ack_id column from the
      // workflow schema instead). Plain string value.
      case 'output_data.data.ack_id': {
        return {
          key: 'ackId',
          header: 'Acknowledgement ID',
          accessor: item => {
            const value = (item.output_data?.data as Record<string, unknown> | undefined)?.ack_id;
            return value ? (
              <span className="whitespace-nowrap font-mono text-xs" title={String(value)}>
                {String(value)}
              </span>
            ) : (
              <span className="text-muted-foreground">N/A</span>
            );
          },
        };
      }

      case 'completed_at':
        return {
          key: 'completedAt',
          header: 'Completed at',
          // Completed cases carry workflow_completed_at; terminated/cancelled cases
          // carry workflow_cancelled_at Anything still in-flight shows "N/A".
          accessor: item => {
            const completedAt = item.workflow_completed_at || item.workflow_cancelled_at;
            return completedAt ? (
              <FormattedDateRelative value={completedAt} />
            ) : (
              <span className="text-muted-foreground">N/A</span>
            );
          },
        };

      case 'agent_name':
        return {
          key: 'agentName',
          header: 'Agent Name',
          accessor: item => {
            const agentName = getCallDetailValue(item, 'agent_name');
            return agentName ? (
              <span className="whitespace-nowrap">{agentName}</span>
            ) : (
              <span className="text-muted-foreground">N/A</span>
            );
          },
          copyable: true,
          copyValue: item => getCallDetailValue(item, 'agent_name') || '',
        };

      case 'reporter_phone':
        return {
          key: 'customerPhoneNumber',
          header: 'Customer Info',
          accessor: item => {
            const phoneNumber =
              getCallDetailValue(item, 'reporter_phone') ||
              getCallDetailValue(item, 'customer_phone_number');
            return phoneNumber ? (
              <span className="whitespace-nowrap font-mono text-xs">{phoneNumber}</span>
            ) : (
              <span className="text-muted-foreground">N/A</span>
            );
          },
        };

      case 'call_duration':
        return {
          key: 'callDuration',
          header: 'Duration',
          accessor: item => {
            const callStartTime = getCallDetailValue(item, 'call_start_time');
            const callEndTime = getCallDetailValue(item, 'call_end_time');
            if (!callStartTime || !callEndTime) {
              return <span className="text-muted-foreground">N/A</span>;
            }
            const startTime = new Date(callStartTime);
            const endTime = new Date(callEndTime);
            // Handle invalid date strings
            if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) {
              return <span className="text-muted-foreground">N/A</span>;
            }
            const diffMs = endTime.getTime() - startTime.getTime();
            const diffSecs = Math.floor(diffMs / 1000);
            const mins = Math.floor(diffSecs / 60);
            const secs = diffSecs % 60;
            if (mins > 0) {
              return (
                <span>
                  {mins}m {secs}s
                </span>
              );
            }
            return <span>{secs}s</span>;
          },
        };

      case 'is_outbound':
        return {
          key: 'isOutbound',
          header: 'Direction',
          accessor: item => {
            const isOutbound = getCallDetailValue(item, 'is_outbound');
            if (isOutbound === undefined || isOutbound === null) {
              return <span className="text-muted-foreground">N/A</span>;
            }
            return isOutbound ? (
              <SimpleTooltip content="Outbound">
                <ArrowRightFromLine className="h-4 w-4 text-blue-600" />
              </SimpleTooltip>
            ) : (
              <SimpleTooltip content="Inbound">
                <ArrowLeftToLine className="h-4 w-4 text-amber-700" />
              </SimpleTooltip>
            );
          },
        };

      case 'disposition_code':
        return {
          key: 'dispositionCode',
          header: 'Disposition',
          accessor: item => {
            // A system code is not a benefit outcome — it is what case_sync stamps when a
            // dial fails (0, 101-106). None of them exist in a workflow's catalog, so the
            // lookup below misses and the cell fell through to the RAW NUMBER: a bare
            // "104" badge. Read as absent, exactly like a null code.
            if (item.disposition_code == null || isSystemDispositionCode(item.disposition_code)) {
              return <span className="text-muted-foreground">N/A</span>;
            }
            const label =
              dispositionsMap?.get(item.disposition_code) ??
              lookupDispositionLabel(resolveRowWorkflowType(item), item.disposition_code);
            return (
              <Badge
                variant="secondary"
                className="whitespace-nowrap font-normal"
                title={`Code: ${item.disposition_code}`}
              >
                {label || item.disposition_code}
              </Badge>
            );
          },
          copyable: true,
          copyValue: item => {
            // Mirrors the cell above: both "no disposition" cases copy as empty.
            if (item.disposition_code == null || isSystemDispositionCode(item.disposition_code)) {
              return '';
            }
            const label =
              dispositionsMap?.get(item.disposition_code) ??
              lookupDispositionLabel(resolveRowWorkflowType(item), item.disposition_code);
            return label || String(item.disposition_code);
          },
        };

      case 'skill_name':
        return {
          key: 'skillName',
          header: 'Skills',
          accessor: item => {
            const skillName = getCallDetailValue(item, 'skill_name');
            return skillName ? (
              <span className="whitespace-nowrap">{skillName}</span>
            ) : (
              <span className="text-muted-foreground">N/A</span>
            );
          },
        };
    }
  }

  // Dynamic input fields
  if (config.type === 'input' || config.type === 'output') {
    return {
      key: config.id,
      header: config.label,
      accessor: item => {
        // MUST come before the get()/isEmpty bail below: a fanned-out leaf has NOTHING at
        // `fieldPath` (pa_status_medbv keeps it inside each drug[]/procedure[] entry), so
        // moving this check down renders N/A on every row.
        if (config.cell) {
          const pills = collectEntityLeafPills(item, config.fieldPath, config.cell);
          if (!pills.length) {
            return <span className="text-muted-foreground">N/A</span>;
          }
          // Reuses the canonical `code · name` badge list rather than a second pill renderer.
          return <DrugOutputFieldPreview data={pills} maxItems={2} />;
        }

        const value = get(item, config.fieldPath);
        // Show "N/A" for empty values
        if (isEmpty(value)) {
          return <span className="text-muted-foreground">N/A</span>;
        }

        // Extract actual field ID from fieldPath (e.g., "input_data.data.j_code" -> "j_code")
        const fieldId = config.fieldPath.split('.').pop() || '';

        // Use preview component if one exists for this field and workflow (registry
        // is source of truth). Resolve workflow_type per-row in cross-program views.
        const PreviewComponent = getPreviewComponent(
          fieldId,
          selectedWorkflow?.workflow_type ?? item.workflow_type,
        );
        if (PreviewComponent) {
          return <PreviewComponent data={value} maxItems={2} />;
        }
        // formatComplexValue handles arrays ("X items"), objects (JSON), and primitives
        return <span className="text-muted-foreground">{formatComplexValue(value)}</span>;
      },
      group: config.group, // Pass the group information
      copyable: true,
      copyValue: item => {
        // Same collector as the accessor, so the copy can never disagree with the pills.
        if (config.cell) {
          return formatEntityLeafCopyValue(
            collectEntityLeafPills(item, config.fieldPath, config.cell),
          );
        }

        const value = get(item, config.fieldPath);
        if (isEmpty(value)) {
          return '';
        }
        return formatComplexValue(value);
      },
    };
  }

  return null;
};

// Define standard columns that are always shown
const getStandardColumns = (
  selectedWorkflow: Workflow | null | undefined,
  includeColumns?: Set<string>,
  activeTabId?: string,
  pqcView?: boolean,
): ColumnConfig[] => {
  // Treat the view as PQC when a single PQC workflow is selected OR a cross-program
  // PQC view forces it (Tier-2 org page, where selectedWorkflow is null).
  const isPqcView = selectedWorkflow?.workflow_type === WorkflowType.PQC || !!pqcView;
  const columns: ColumnConfig[] = [
    { id: 'case_id', fieldPath: 'id', label: 'Case ID', type: 'standard', enabled: true, order: 0 },
  ];

  if (!isPqcView) {
    columns.push({
      id: 'reference_id',
      fieldPath: 'reference_id',
      label: 'Reference ID',
      type: 'standard',
      enabled: true,
      order: 1,
    });
  }

  // Cross-program opt-in columns slot in after Case ID / Reference ID so they
  // appear before assignee/status without disturbing the existing order in
  // CaseList (where they're absent).
  if (includeColumns?.has('program')) {
    columns.push({
      id: 'program',
      fieldPath: 'program',
      label: 'Program',
      type: 'standard',
      enabled: true,
      order: 3,
    });
  }
  if (includeColumns?.has('workflow_type')) {
    columns.push({
      id: 'workflow_type',
      fieldPath: 'workflow_type',
      label: 'Type',
      type: 'standard',
      enabled: true,
      order: 4,
    });
  }
  if (includeColumns?.has('manufacturer')) {
    columns.push({
      id: 'manufacturer',
      fieldPath: 'manufacturer',
      label: 'Manufacturer',
      type: 'standard',
      enabled: true,
      order: 4,
    });
  }
  if (includeColumns?.has('payor')) {
    columns.push({
      id: 'payor',
      fieldPath: 'payor',
      label: 'Payor',
      type: 'standard',
      enabled: true,
      order: 5,
    });
  }

  columns.push(
    {
      id: 'assigned',
      fieldPath: 'assigned',
      label: 'Assignee',
      type: 'standard',
      enabled: true,
      order: 2,
    },
    {
      id: 'created_by',
      fieldPath: 'created_by',
      label: 'Created by',
      type: 'standard',
      enabled: true,
      // Org-wide Cases places Created by right after Assignee (order 2);
      // the standard (flag-off) view keeps it at the trailing position.
      order: pqcView ? 3 : 999,
    },
    {
      id: 'created_at',
      fieldPath: 'created_at',
      label: 'Created at',
      type: 'standard',
      enabled: true,
      order: 1000,
    },

    {
      id: 'completed_at',
      fieldPath: 'completed_at',
      label: 'Completed at',
      type: 'standard',
      enabled: true,
      order: 1000.5,
    },
  );

  // Hidden on PQC views, where call attempts aren't relevant.
  if (includeColumns?.has('total_call_attempts') && !isPqcView) {
    columns.push({
      id: 'total_call_attempts',
      fieldPath: 'total_call_attempts',
      label: 'Total Call Attempts',
      type: 'standard',
      enabled: true,
      order: 1000.7,
    });
  }

  if (DISPOSITION_WORKFLOW_TYPES.includes(selectedWorkflow?.workflow_type ?? '')) {
    columns.push({
      id: 'disposition_code',
      fieldPath: 'disposition_code',
      label: 'Disposition',
      type: 'standard',
      enabled: true,
      order: 1001,
    });
  }

  // Hide timer column for recall_scheduler workflow.
  // For PQC, slot the SLA Status column right after Case ID so the sticky-left pinning
  // (handled by GenericTable when key === 'sla_status') visually anchors urgency next
  // to the identifier. Other workflows keep the legacy right-side position.
  if (selectedWorkflow?.workflow_type !== WorkflowType.RECALL_SCHEDULER) {
    const isPqc = isPqcView;
    // The SLA clock stops at submission, so for PQC the Time Remaining column is
    // only meaningful on Pending (live countdown) and All (mixed states); the
    // submitted-state and terminal tabs omit it.
    // The pending/all-only restriction is a Org-wide Cases refinement; the
    // standard (flag-off) view keeps the timer on every tab as before.
    const showPqcTimer = !pqcView || activeTabId === 'pending' || activeTabId === 'all';
    if (!isPqc || showPqcTimer) {
      columns.push({
        id: 'timer',
        fieldPath: '',
        label: 'Timer',
        type: 'standard',
        enabled: true,
        order: isPqc ? 1 : 1002,
      });
    }
  }

  // PQC lifecycle timestamp columns — hardcoded per-tab (not customizable), shown
  // only on the tab where the value is meaningful. A special timer sits just
  // before Created at (order 1000). The Ack Id column is sourced from the schema
  // output ("Acknowledgement ID") in createCasesTableConfig, not here.
  // Gated on the Org-wide Cases (org-wide Tier-2 view) only — the standard
  // per-program view never shows these lifecycle timestamp columns.
  if (pqcView) {
    // Ack Received (AQ-191): Ready to Complete.
    if (activeTabId === 'ready-to-complete') {
      columns.push({
        id: 'ack_received_at',
        fieldPath: 'output_data.data.ack_received_at',
        label: 'Ack Received',
        type: 'standard',
        enabled: true,
        order: 998,
      });
    }
    // Follow-up Requested (AQ-301): Follow-up Required.
    if (activeTabId === 'followup') {
      columns.push({
        id: 'follow_up_requested_at',
        fieldPath: 'output_data.data.follow_up_requested_at',
        label: 'Follow-up Requested',
        type: 'standard',
        enabled: true,
        order: 998,
      });
    }
    // Acknowledgement ID: on the cross-program org view there's no single-workflow
    // schema to source it from (the program page adds it from columnConfig in
    // createCasesTableConfig), so hardcode it on the ack tabs. Guard on
    // !selectedWorkflow to avoid duplicating the schema-sourced column on the
    // program page.
    if (!selectedWorkflow && (activeTabId === 'ready-to-complete' || activeTabId === 'completed')) {
      columns.push({
        id: 'ack_id',
        fieldPath: 'output_data.data.ack_id',
        label: 'Acknowledgement ID',
        type: 'standard',
        enabled: true,
        order: 999,
      });
    }
    // Submitted At (AQ-192): Submitted tab — derived from state_updated_at.
    if (activeTabId === 'submitted') {
      columns.push({
        id: 'submitted_at',
        fieldPath: 'state_updated_at',
        label: 'Submitted At',
        type: 'standard',
        enabled: true,
        order: 998,
      });
    }
  }

  columns.push({
    id: 'state',
    fieldPath: 'state',
    label: 'Status',
    type: 'standard',
    enabled: true,
    order: 1003,
  });

  // Hide row actions (notes menu) for PQC — including the cross-program org PQC view.
  if (!isPqcView) {
    columns.push({
      id: 'row_actions',
      fieldPath: 'row_actions',
      label: '',
      type: 'standard',
      enabled: true,
      order: 1004,
    });
  }

  return columns;
};

// Cases table configuration with dynamic columns support
export const createCasesTableConfig = (
  selectedWorkflow?: Workflow | null,
  columnConfig?: ColumnConfig[], // New optional parameter for input fields only
  onActionSelect?: (action: string, caseId: string, actionDataParams?: ActionDataParams) => void, // Callback for handling actions with confirmation
  onAssigneeSelect?: (assigneeUserId: string, caseId: string, teamId?: string) => void, // Callback for handling assignee changes
  getRowLink?: (item: GetCaseRecordResponse) => string, // Optional custom row link generator
  getRowLinkState?: (item: GetCaseRecordResponse) => unknown, // Optional state to pass to Link
  usersMap?: Map<string, User>, // User lookup map for created_by column
  usersLoading?: boolean, // Whether user lookup is still in progress
  excludeColumns?: string[], // Column IDs to exclude from the table
  dispositionsMap?: Map<number, string>, // Disposition code → label lookup map
  options?: {
    /** Opt-in IDs from OPT_IN_COLUMN_IDS to include in the table (e.g. 'program',
     * 'workflow_type', 'payor'). Used by cross-program views like My Cases. */
    includeColumns?: string[];
    /** Resolvers for cross-program data the row itself doesn't carry. */
    context?: CasesTableContext;
    /** Active quick-filter tab id — drives hardcoded per-tab PQC columns. */
    activeTabId?: string;
  },
): TableConfig<GetCaseRecordResponse> => {
  const includeColumnSet = options?.includeColumns
    ? new Set(
        options.includeColumns.filter(id => (OPT_IN_COLUMN_IDS as readonly string[]).includes(id)),
      )
    : undefined;
  const context = options?.context;

  // Get standard columns (always shown)
  const standardColumns = getStandardColumns(
    selectedWorkflow,
    includeColumnSet,
    options?.activeTabId,
    context?.pqcView,
  );

  // PQC Ack ID: instead of a hardcoded column, surface the schema-defined
  // "Acknowledgement ID" output column (same output_data.data.ack_id path) at the
  // tail — just before Status — on the tabs where an acknowledgement is
  // expected/present. Sourced from the workflow output schema so it keeps the
  // schema label and stays in sync.
  const PQC_ACK_TABS = new Set(['ready-to-complete', 'completed']);
  const pqcAckColumn =
    // Org-wide Cases only — flag-off per-program view keeps the standard
    // columns (no schema-sourced Ack ID column).
    context?.pqcView &&
    selectedWorkflow?.workflow_type === WorkflowType.PQC &&
    options?.activeTabId &&
    PQC_ACK_TABS.has(options.activeTabId)
      ? columnConfig?.find(
          col => col.type === 'output' && col.fieldPath === 'output_data.data.ack_id',
        )
      : undefined;
  if (pqcAckColumn) {
    standardColumns.push({ ...pqcAckColumn, enabled: true, order: 1001 });
  }

  // Filter and prepare customizable columns (user-selected)
  // Includes input/output fields and optional standard columns (e.g., created_by)
  // Sort by order to maintain user's selection order (first selected appears first)
  const inputAndOutputColumns: ColumnConfig[] = columnConfig
    ? columnConfig
        .filter(col => {
          // The Ack ID column is force-added to the tail above on ack tabs — drop
          // it here so it isn't duplicated in the customizable section.
          if (pqcAckColumn && col.id === pqcAckColumn.id) return false;
          const isEnabled = col.enabled;
          const isInput = col.type === 'input';
          const isOutput = col.type === 'output';
          const isOptionalStandard = col.type === 'standard';
          return isEnabled && (isInput || isOutput || isOptionalStandard);
        })
        .sort((a, b) => a.order - b.order) // Sort by user's selection order
        .map((col, index) => ({
          ...col,
          order: standardColumns.length + index, // Place after standard columns (6, 7, 8...)
        }))
    : [];

  // Combine standard, input, and output columns, excluding any specified columns
  const allColumns = [...standardColumns, ...inputAndOutputColumns].filter(
    col => !excludeColumns?.includes(col.id),
  );

  // Build table columns from combined config
  const columns: TableColumnConfig<GetCaseRecordResponse>[] = allColumns
    .sort((a, b) => a.order - b.order)
    .map(col =>
      createColumnFromConfig({
        config: col,
        selectedWorkflow,
        onActionSelect,
        onAssigneeSelect,
        usersMap,
        usersLoading,
        dispositionsMap,
        context,
      }),
    )
    .filter((col): col is TableColumnConfig<GetCaseRecordResponse> => col !== null);

  return {
    columns,
    getRowLink: getRowLink ?? (item => `./${item.id}`),
    getRowLinkState,
    getRowKey: item => item.id || '',
    emptyState: {
      title: 'No cases found',
      description:
        'There are no cases matching the selected filters. Try adjusting the filters or create a new case.',
    },
  };
};

// Campaigns table configuration
export const createCampaignsTableConfig = (
  usersMap?: Map<string, User>,
): TableConfig<Campaign> => ({
  columns: [
    {
      key: 'name',
      header: 'Campaign name',
      accessor: item => <span className="text-foreground">{item.name}</span>,
    },
    {
      key: 'createdBy',
      header: 'Created by',
      accessor: item => {
        const userId = item.created_by;
        return (
          <UserCell
            user={userId ? usersMap?.get(userId) : undefined}
            fallbackId={userId}
            nameOnly
          />
        );
      },
    },
    {
      key: 'createdAt',
      header: 'Created at',
      accessor: item =>
        item.created_at ? (
          <FormattedDateRelative value={item.created_at} />
        ) : (
          <span className="text-muted-foreground">N/A</span>
        ),
    },
    {
      key: 'caseCount',
      header: 'Number of cases',
      accessor: item => <span className="text-foreground">{item.total_cases || '-'}</span>,
    },
  ],
  getRowLink: item => `./${item.id}`,

  getRowKey: item => item.id || '',
  emptyState: {
    title: 'No campaigns found',
    description: 'Create a new campaign to bulk upload cases.',
  },
});
